import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { AppData } from '../AppData';
import { CompoApp } from '../CompoApp';
import { UrlBuilder } from '../../models/frontend_entities/UrlBuilder';
import Constants from '../../Constants';
import { ComponentClassGroupDto } from '../../models/component-class-group/component-class-group-dto.model';
import { ComponentClassGroupInfoDto } from '../../models/component-class-group/component-class-group-create-dto.model';
import { ModuleAttributeWithOptionDto } from '../../models/module_attribute/ModuleAttributeWithOptionDto';

@Injectable({
  providedIn: 'root'
})
export class ComponentClassGroupRequestService {

  //region INJECTIONS
  private readonly data: AppData = inject(CompoApp).data;
  private readonly http: HttpClient = inject(HttpClient);
  //endregion

  //region GET
  public getById(id: string): Observable<ComponentClassGroupDto> {
    const url: string = new UrlBuilder(
      Constants.URLS.componentClassGroup.byId.replace('{id}', id)
    )
      .param('accessMode', this.data.getAccessMode())
      .build();

    return this.http.get<ComponentClassGroupDto>(url).pipe(take(1));
  }

  public getAssignedClasses(id: string): Observable<string[]> {
    const url: string = new UrlBuilder(
      Constants.URLS.componentClassGroup.classes.replace('{id}', id)
    )
      .param('accessMode', this.data.getAccessMode())
      .build();

    return this.http.get<string[]>(url).pipe(take(1));
  }

  public getScopeAssignedClasses(id: string): Observable<string[]> {
    const url: string = new UrlBuilder(
      Constants.URLS.componentClassGroup.scopeAssignedClasses.replace('{id}', id)
    )
      .param('accessMode', this.data.getAccessMode())
      .build();

    return this.http.get<string[]>(url).pipe(take(1));
  }

  public getModuleAttributes(id: string): Observable<ModuleAttributeWithOptionDto[]> {
    const url: string = new UrlBuilder(
      Constants.URLS.componentClassGroup.moduleAttributes.replace('{id}', id)
    )
      .param('accessMode', this.data.getAccessMode())
      .build();

    return this.http.get<ModuleAttributeWithOptionDto[]>(url).pipe(take(1));
  }
  //endregion

  //region POST
  public create(dto: ComponentClassGroupInfoDto): Observable<ComponentClassGroupDto> {
    const url: string = new UrlBuilder(
      Constants.URLS.componentClassGroup.baseUrl
    )
      .param('accessMode', this.data.getAccessMode())
      .build();

    return this.http.post<ComponentClassGroupDto>(url, dto).pipe(take(1));
  }

  public saveAssignedClasses(id: string, classIds: string[]): Observable<void> {
    const url: string = new UrlBuilder(
      Constants.URLS.componentClassGroup.classes.replace('{id}', id)
    )
      .param('accessMode', this.data.getAccessMode())
      .build();

    return this.http.post<void>(url, classIds).pipe(take(1));
  }
  //endregion

  //region PUT
  public update(id: string, dto: ComponentClassGroupInfoDto): Observable<ComponentClassGroupDto> {
    const url: string = new UrlBuilder(
      Constants.URLS.componentClassGroup.byId.replace('{id}', id)
    )
      .param('accessMode', this.data.getAccessMode())
      .build();

    return this.http.put<ComponentClassGroupDto>(url, dto).pipe(take(1));
  }
  //endregion

  //region DELETE
  public delete(id: string): Observable<void> {
    const url: string = new UrlBuilder(
      Constants.URLS.componentClassGroup.byId.replace('{id}', id)
    )
      .param('accessMode', this.data.getAccessMode())
      .build();

    return this.http.delete<void>(url).pipe(take(1));
  }
  //endregion
}
